// src/pages/secret/SecretLeasesPage.tsx
// Leases — cross-engine, time-limited dynamic credentials. List, renew, revoke.
// See docs/ux/secret-ia.md section 2. Admin-gated (AdminRoute, in App.tsx).

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, useSecretLease } from '@react/hooks';
import { secretApi } from '@core/api';
import { Button, Card, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { StatusBadge } from '@/components/StatusBadge';
import { TypedConfirmDialog } from '@/features/secret/TypedConfirmDialog';
import type { Lease } from '@core/types/secret';
import { leaseStatusVariant, leaseStatusLabelKey, canRenewLease, canRevokeLease, formatDateTime, formatTtlSeconds } from '@/features/secret/logic';

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'LEASE_STATUS_ACTIVE', label: 'Active' },
  { value: 'LEASE_STATUS_EXPIRED', label: 'Expired' },
  { value: 'LEASE_STATUS_REVOKED', label: 'Revoked' },
];

export const SecretLeasesPage = observer(() => {
  const { t } = useI18n();
  const store = useSecretLease();
  const [leases, setLeases] = useState<Lease[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [revokeTarget, setRevokeTarget] = useState<Lease | null>(null);
  const [isActing, setIsActing] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    secretApi.ListLeases(statusFilter ? { status: statusFilter } : undefined)
      .then((resp) => setLeases(resp.leases ?? []))
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const doRenew = async (lease: Lease) => {
    setIsActing(lease.lease_id);
    try {
      await store.RenewLease(lease.lease_id, { lease_id: lease.lease_id });
      load();
    } finally {
      setIsActing(null);
    }
  };

  const doRevoke = async () => {
    if (!revokeTarget) return;
    setIsActing(revokeTarget.lease_id);
    try {
      await store.RevokeLease(revokeTarget.lease_id, { lease_id: revokeTarget.lease_id });
      setRevokeTarget(null);
      load();
    } finally {
      setIsActing(null);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('secret.leases.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('secret.leases.subtitle')}</p>
        </div>
        <Link to="/secrets" className="text-sm text-primary hover:underline">{t('secret.console.backToConsole')}</Link>
      </div>

      {error != null && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      <div className="max-w-xs">
        <Select
          label={t('secret.leases.filterStatus')}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={STATUS_OPTIONS}
        />
      </div>

      <TypedConfirmDialog
        open={!!revokeTarget}
        title={t('secret.leases.revokeTitle')}
        description={t('secret.leases.revokeDescription')}
        phrase={revokeTarget?.lease_id ?? ''}
        confirmLabel={t('secret.leases.revoke')}
        isConfirming={!!isActing}
        onConfirm={doRevoke}
        onCancel={() => setRevokeTarget(null)}
      />

      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('secret.leases.column.id')}</TableHead>
              <TableHead>{t('secret.leases.column.status')}</TableHead>
              <TableHead>{t('secret.leases.column.ttl')}</TableHead>
              <TableHead>{t('secret.leases.column.expires')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
            ) : leases.length === 0 ? (
              <TableEmpty colSpan={5} message={t('secret.leases.empty')} />
            ) : (
              leases.map((lease) => (
                <TableRow key={lease.lease_id}>
                  <TableCell className="font-mono text-xs">{lease.lease_id}</TableCell>
                  <TableCell><StatusBadge variant={leaseStatusVariant(lease.status)} label={t(leaseStatusLabelKey(lease.status))} /></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatTtlSeconds(lease.ttl_seconds)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDateTime(lease.expire_time)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canRenewLease(lease) && (
                        <Button variant="ghost" size="sm" onClick={() => doRenew(lease)} disabled={isActing === lease.lease_id}>{t('secret.leases.renew')}</Button>
                      )}
                      {canRevokeLease(lease) && (
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setRevokeTarget(lease)}>{t('secret.leases.revoke')}</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default SecretLeasesPage;
